import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { budgetsApi } from "../../services/api";
import { Budget } from "@akfdev-com/shared/src/models";
import Spinner from "../common/Spinner";
import ErrorAlert from "../common/ErrorAlert";

interface BuildingBudgetsProps {
  buildingId: string;
}

const formatCurrency = (pence: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    (pence ?? 0) / 100,
  );

const BuildingBudgets: React.FC<BuildingBudgetsProps> = ({ buildingId }) => {
  const [budgets, setBudgets] = useState<Budget[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await budgetsApi.getAll(buildingId);
      const items: Budget[] = Array.isArray(data) ? data : data?.items ?? [];
      setBudgets(items);
    } catch (err) {
      console.error("[BuildingBudgets] Failed to load budgets:", err);
      setError("Failed to load budgets. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="p-8 flex justify-center">
          <Spinner />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-4">
          <ErrorAlert message={error} />
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={load}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    if (!budgets || budgets.length === 0) {
      return (
        <div className="p-8 text-center">
          <p className="text-gray-500 mb-4">
            No budgets have been created yet.
          </p>
          <Link
            to={`/dashboard/budgets/new?building=${buildingId}`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            Create Budget
          </Link>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Year
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Period
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Spent
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Remaining
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {budgets.map((budget) => {
              const total = budget.totalAmount ?? 0;
              const spent = budget.actualSpent ?? 0;
              const remaining = total - spent;
              return (
                <tr key={budget.id}>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {budget.year}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {budget.startDate
                      ? new Date(budget.startDate).toLocaleDateString()
                      : "-"}{" "}
                    →{" "}
                    {budget.endDate
                      ? new Date(budget.endDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900 text-right">
                    {formatCurrency(total)}
                  </td>
                  <td className="px-4 py-2 text-sm text-red-600 text-right">
                    {formatCurrency(spent)}
                  </td>
                  <td className="px-4 py-2 text-sm text-green-700 text-right">
                    {formatCurrency(remaining)}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 capitalize">
                      {budget.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-right">
                    <Link
                      to={`/dashboard/budgets/${budget.id}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900">Budgets</h3>
        <Link
          to={`/dashboard/budgets/new?building=${buildingId}`}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none"
        >
          Create Budget
        </Link>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {renderContent()}
      </div>
    </div>
  );
};

export default BuildingBudgets;
